/**
 * App Component
 *
 * Pure layout wrapper with background styling and dimensions.
 * Route definitions moved to src/routes/routes.tsx.
 */

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { backgroundImage, topLeftBg, topRightBg, bottomLeftBg, bottomRightBg } from '@/assets';
import { Router } from '@/routes';
import { AuthProvider } from '@/features/auth/contexts/AuthContext';
import { AuthErrorBoundary } from '@/features/auth/components/AuthErrorBoundary';
import { AppErrorBoundary } from '@/shared/components/AppErrorBoundary';
import { useLanguageSync } from '@/i18n/useLanguageSync';
import { AudioManagerProvider } from '@/shared/components/AudioManagerProvider';
import { PWAInstallDialog } from '@/shared/components/PWAInstallDialog';
import { usePWAInstall } from '@/shared/hooks/usePWAInstall';
import { clearChunkReloadFlag } from '@/shared/utils/lazyWithRetry';

function App() {
  // Sync i18next language with Zustand store
  useLanguageSync();

  // Clear chunk reload flag on successful mount (app loaded fine)
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  // PWA install prompt (manually controlled, not auto-popup)
  const { dialogOpen, closeDialog, isSafari, install } = usePWAInstall();

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthErrorBoundary>
          <AuthProvider>
            {/* Audio manager initializes at app root, inside BrowserRouter for useLocation */}
            <AudioManagerProvider>
              {/* Outer wrapper with relative positioning */}
              <div className="min-h-screen relative overflow-hidden">
                {/* Blurred background layer - only visible on tablet/desktop */}
                <div
                  className="absolute inset-0 -z-10"
                  style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(8px)',
                    WebkitFilter: 'blur(8px)'
                  }}
                />
                {/* Inner container - sharp football field background */}
                <div
                  className="relative mx-auto h-screen overflow-hidden shadow-2xl flex flex-col rounded-xl w-screen md:w-[min(calc(100vh*0.462),100vw)]"
                  style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Spectator backgrounds in corners */}
                  <div className="absolute inset-0 z-[1] pointer-events-none">
                    <img
                      src={topLeftBg}
                      alt=""
                      className="absolute top-0 left-0 w-auto h-auto max-w-[40%] max-h-[35%] object-contain"
                    />
                    <img
                      src={topRightBg}
                      alt=""
                      className="absolute top-0 right-0 w-auto h-auto max-w-[40%] max-h-[35%] object-contain"
                    />
                    <img
                      src={bottomLeftBg}
                      alt=""
                      className="absolute bottom-0 left-0 w-auto h-auto max-w-[40%] max-h-[35%] object-contain"
                    />
                    <img
                      src={bottomRightBg}
                      alt=""
                      className="absolute bottom-0 right-0 w-auto h-auto max-w-[40%] max-h-[35%] object-contain"
                    />
                  </div>
                  {/* Routes - direct children of container.
                      Story 04-08: the Ably client is a module-level singleton
                      (lib/ably-client.ts), not a React provider mounted in this
                      tree — so there is no "Ably provider" to hoist above
                      GamePage's BoardErrorBoundary. What matters instead is
                      that every hook owning a subscription/presence membership
                      (useGamePresenceManager, useGameSync, useConnectionStatus)
                      is called from useGamePageViewModel in GamePage itself, a
                      sibling of — not a descendant of — BoardErrorBoundary. A
                      board-only catch unmounts the board subtree; it never
                      unmounts GamePage or anything Router renders here. */}
                  <div className="relative z-10 flex-1 flex flex-col">
                    <Router />
                  </div>

                  {/* PWA Install Dialog - manually opened from Settings */}
                  <PWAInstallDialog
                    isOpen={dialogOpen}
                    isSafari={isSafari}
                    onInstall={() => void install()}
                    onClose={closeDialog}
                  />
                </div>
              </div>
            </AudioManagerProvider>
          </AuthProvider>
        </AuthErrorBoundary>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
